import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Phone, Add, PhoneInTalk } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import toast from 'react-hot-toast';
import { getApiErrorMessage, toUserErrorMessage } from '../../utils/apiError';
import { getAuthToken } from '../../lib/authToken';
import LiveCallDialog from '../Calls/LiveCallDialog';

interface CallLogPanelProps {
  /** Case Folder: required. Client Folder: omit or null. */
  caseId?: string | null;
  clientId?: string | null;
}

const emptyForm = () => ({
  direction: 'outbound',
  fromNumber: '',
  toNumber: '',
  durationSeconds: 0,
  notes: '',
  createTimeEntry: true,
});

const CallLogPanel: React.FC<CallLogPanelProps> = ({ caseId = null, clientId = null }) => {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [liveOpen, setLiveOpen] = useState(false);

  // Wave CM1 — live portal calls (same query key as LiveCallDialog, so it is shared).
  const { data: liveConfig } = useQuery({
    queryKey: ['live-calls', 'config'],
    enabled: Boolean(clientId),
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const res = await fetch('/api/live-calls/config', { headers: { Authorization: `Bearer ${getAuthToken()}` } });
      if (!res.ok) return null;
      const json = await res.json();
      return json.data || null;
    },
  });
  const canPortalCall = Boolean(clientId && liveConfig?.portalCalls);

  const scopeKey = caseId ? `case:${caseId}` : `client:${clientId}`;
  const canQuery = Boolean(caseId || clientId);

  const { data: calls = [], isLoading, error } = useQuery({
    queryKey: ['calls', scopeKey],
    enabled: canQuery,
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '100' });
      if (caseId) params.set('caseId', caseId);
      else if (clientId) params.set('clientId', clientId);
      const res = await fetch(`/api/calls?${params.toString()}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Unable to load call logs'));
      const json = await res.json();
      return json.data?.calls || [];
    },
  });

  const logMutation = useMutation({
    mutationFn: async () => {
      if (!caseId && !clientId) {
        throw new Error('Case or client is required to log a call');
      }
      const res = await fetch('/api/calls/log', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          caseId: caseId || undefined,
          clientId: clientId || undefined,
          direction: form.direction,
          fromNumber: form.fromNumber || undefined,
          toNumber: form.toNumber || undefined,
          durationSeconds: Number(form.durationSeconds) || 0,
          notes: form.notes || undefined,
          createTimeEntry: form.createTimeEntry,
          startedAt: new Date().toISOString(),
        }),
      });
      if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Failed to log call'));
      return res.json();
    },
    onSuccess: () => {
      toast.success('Call logged');
      queryClient.invalidateQueries({ queryKey: ['calls', scopeKey] });
      setDialogOpen(false);
      setForm(emptyForm());
    },
    onError: (err: Error) => toast.error(toUserErrorMessage(err, 'Failed to log call')),
  });

  const formatDuration = (seconds?: number) => {
    const s = Number(seconds) || 0;
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${String(r).padStart(2, '0')}`;
  };

  if (!canQuery) {
    return (
      <Alert severity="info">Save the client first to log calls.</Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Phone fontSize="small" /> Call log
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canPortalCall && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<PhoneInTalk />}
              onClick={() => setLiveOpen(true)}
            >
              Call in portal
            </Button>
          )}
          <Button
            variant="contained"
            size="small"
            startIcon={<Add />}
            onClick={() => setDialogOpen(true)}
          >
            Log call
          </Button>
        </Box>
      </Box>

      {clientId && liveConfig && !liveConfig.portalCalls && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          Calling clients through the portal is turned off for your firm. A firm administrator can turn it on under
          Security → Edit Policy → Portal calls.
        </Typography>
      )}

      {liveOpen && clientId && (
        <LiveCallDialog
          open={liveOpen}
          onClose={() => setLiveOpen(false)}
          clientId={clientId}
          caseId={caseId}
        />
      )}

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {toUserErrorMessage(error, 'Unable to load call logs')}
        </Alert>
      )}

      {!isLoading && !error && calls.length === 0 && (
        <Card variant="outlined">
          <CardContent>
            <Typography color="text.secondary">
              No calls logged yet. Use Log call to record a phone conversation
              {caseId ? ' on this matter' : ' for this client'}.
            </Typography>
          </CardContent>
        </Card>
      )}

      {!isLoading && calls.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Direction</TableCell>
                <TableCell>From / To</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {calls.map((call: any) => (
                <TableRow key={call.id}>
                  <TableCell>
                    {call.startedAt
                      ? format(parseISO(call.startedAt), 'MMM d, yyyy h:mm a')
                      : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={call.direction || '—'}
                      color={call.direction === 'inbound' ? 'info' : 'default'}
                    />
                    {call.provider === 'portal_webrtc' && (
                      <Chip size="small" variant="outlined" color="primary" label="Portal" sx={{ ml: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell>
                    {[call.fromNumber, call.toNumber].filter(Boolean).join(' → ') || '—'}
                  </TableCell>
                  <TableCell>{formatDuration(call.durationSeconds)}</TableCell>
                  <TableCell>{call.notes || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Log call</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Direction</InputLabel>
                <Select
                  label="Direction"
                  value={form.direction}
                  onChange={(e) => setForm((f) => ({ ...f, direction: e.target.value }))}
                >
                  <MenuItem value="outbound">Outbound</MenuItem>
                  <MenuItem value="inbound">Inbound</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Duration (seconds)"
                type="number"
                fullWidth
                value={form.durationSeconds}
                onChange={(e) =>
                  setForm((f) => ({ ...f, durationSeconds: parseInt(e.target.value, 10) || 0 }))
                }
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="From number"
                fullWidth
                value={form.fromNumber}
                onChange={(e) => setForm((f) => ({ ...f, fromNumber: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="To number"
                fullWidth
                value={form.toNumber}
                onChange={(e) => setForm((f) => ({ ...f, toNumber: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notes"
                fullWidth
                multiline
                rows={3}
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.createTimeEntry}
                    onChange={(e) => setForm((f) => ({ ...f, createTimeEntry: e.target.checked }))}
                  />
                }
                label="Create billable time entry"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => logMutation.mutate()}
            disabled={logMutation.isPending}
          >
            {logMutation.isPending ? 'Saving…' : 'Save call'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CallLogPanel;
